import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { latestArticles } from '@/mocks/homeData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = latestArticles.find((a) => a.slug === slug) || latestArticles[0];
  const relatedArticles = latestArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '芒果视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.mangoshipin.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mangoshipin.com/' },
      { '@type': 'ListItem', position: 2, name: '文章', item: 'https://www.mangoshipin.com/' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 芒果视频`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        ogTitle={`${article.title} - 芒果视频`}
        ogDescription={article.excerpt}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />

      <main className="pt-20">
        <article className="w-full px-4 md:px-6 max-w-3xl mx-auto py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-sm"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </div>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-sm"></i>
                </div>
                {article.readTime}阅读
              </span>
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
            </div>
          </header>

          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
              title={`${article.title} - 芒果视频`}
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mt-8 mb-4">
              详细内容
            </h2>

            <p className="mb-4 text-foreground-700 leading-relaxed">
              作为{article.category === '平台对比' ? '视频平台爱好者' : '深度评测用户'}，我们花了大量时间对{article.tags.includes('蜜桃视频') ? '芒果视频、蜜桃视频和糖豆视频' : article.tags.includes('蘑菇视频') ? '芒果视频、蘑菇视频和蓝莓视频' : '芒果视频'}进行了全面的测试和体验。以下是我们详细的评测结果和使用感受。
            </p>

            <h3 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading mt-6 mb-3">
              一、平台概况
            </h3>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              芒果视频作为国内新兴的视频平台，自上线以来就以丰富的资源和流畅的播放体验赢得了大量用户的青睐。与蜜桃视频、糖豆视频等同类平台相比，芒果视频在内容覆盖面和用户界面设计上投入了更多精力。平台涵盖了电影、电视剧、综艺、动漫、纪录片、体育赛事、音乐MV以及教育课程等八大内容板块，无论您喜欢什么类型的视频内容，都能在这里找到满意的选择。
            </p>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              芒果视频App的界面设计简洁大方，首页推荐算法精准，能够根据用户的观看历史和偏好推送相关内容。搜索功能强大，支持按名称、演员、导演、类型等多种方式筛选。视频详情页信息完整，包含简介、评分、演职员表等内容，方便用户快速了解影片信息。
            </p>

            <h3 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading mt-6 mb-3">
              二、画质与播放体验
            </h3>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              在画质方面，芒果视频支持从480P到4K的多种清晰度选择。免费用户即可享受1080P高清画质，这在同类型平台中属于较高水准。我们测试了多部热门影片，画面色彩还原准确，细节表现出色。特别是在大屏设备上观看时，4K超清画质带来的视觉冲击力令人印象深刻。
            </p>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              播放流畅度是芒果视频的另一大亮点。平台采用先进的CDN内容分发技术，在全国范围内部署了多个服务器节点。我们分别在Wi-Fi和移动网络环境下进行了测试，视频加载速度平均在1.5秒以内，播放过程中几乎没有出现过卡顿现象。智能码率调节功能也能根据网络状况自动切换清晰度，确保观影不中断。
            </p>

            <h3 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading mt-6 mb-3">
              三、资源丰富度
            </h3>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              芒果视频的资源库相当丰富，目前收录了超过五万部影视内容。无论是当下热门的新片，还是经典老片，都能在平台上找到。平台每天都会更新大量新内容，热门电视剧和综艺节目的更新速度与主要视频平台保持一致，有些甚至更快。独家内容专区也提供了不少优质原创作品，值得一看。
            </p>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              相比蘑菇视频和蓝莓视频，芒果视频在资源数量和质量上都有明显优势。蘑菇视频主要侧重于特定类型内容，而蓝莓视频的资源库相对较小。芒果视频则做到了全面覆盖，无论你是电影发烧友、追剧达人还是综艺爱好者，都能在这里找到心仪的内容。
            </p>

            <h3 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading mt-6 mb-3">
              四、总结与推荐
            </h3>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              综合来看，芒果视频是一款非常值得推荐的视频平台。它在资源丰富度、播放流畅度、画质表现和用户体验等方面都表现出色。无论是与蜜桃视频、糖豆视频等传统平台对比，还是与蘑菇视频、蓝莓视频等新兴平台竞争，芒果视频都展现出了独特的优势。
            </p>
            <p className="mb-4 text-foreground-700 leading-relaxed">
              对于追求高品质观影体验的用户来说，芒果视频无疑是最佳选择之一。免费用户即可享受高清观影体验，会员用户更能获得4K超清、免广告等增值服务。如果你还在纠结选择哪个视频平台，不妨试试芒果视频，相信它不会让你失望。
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 font-heading">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}